import Category from './Category/Category';
import CategorySkeleton from './CategorySkeleton/CategorySkeleton';

import styles from './CategoryList.module.scss';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useTranslation } from 'react-i18next';
import { Pagination } from '~/ui';
import cn from 'classnames';
import { useState, useEffect, useRef } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

export interface IKnowledge {
  id: number;
  asset_id: number;
  name: string;
  description: string;
  svg_icon: string;
  articles_count: number;
}

interface SelectedPage {
  selected: number;
}

interface IDataProps {
  data: IKnowledge[];
  mobile?: boolean;
  page?: number;
  currentPage?: number;
  setPage?: (value: number) => void;
  isLoading?: boolean;
}

const CategoryList = ({ data, mobile = false, page, currentPage, setPage, isLoading }: IDataProps) => {
  const { t } = useTranslation();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const [items, setItems] = useState<IKnowledge[]>(data ?? []);
  const wasDragging = useRef(false);

  useEffect(() => {
    setItems(data ?? []);
  }, [data]);

  const client = useQueryClient();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const { mutate: saveSort } = useMutation(
    async (ids: number[]) => {
      const response = await instance.post('admin/knowledge-base-categories/sort', { ids });
      return response.data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(['knowledge-base']);
      },
      onError: error => {
        toastError(error);
        setItems(data ?? []);
      },
    }
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (wasDragging.current) {
      const cancelClick = (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        document.removeEventListener('click', cancelClick, true);
      };
      document.addEventListener('click', cancelClick, true);
      wasDragging.current = false;
    }

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex(i => i.id === active.id);
    const newIndex = items.findIndex(i => i.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    saveSort(reordered.map(i => i.id));
  };

  const { mutateAsync: deleteCategory } = useMutation(
    async (id: number) => {
      const response = await instance.delete(
        `admin/knowledge-base-categories/${id}`
      );

      return response.data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(['knowledge-base']);
        toastSuccess(t('category_successfully_deleted'));
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const handleSetPage = (selectedPage: SelectedPage) => {
    setPage?.(selectedPage.selected + 1);
  };

  return (
    <div className={styles.listWrapper}>
      {mobile ? (
        <ul className={cn(styles.mobileCategoryList, styles.categoryListLoaded)}>
          {isLoading ? (
            <CategorySkeleton count={6} />
          ) : (
            items.map(item => (
              <Category key={item.id} data={item} onClick={deleteCategory} mobile />
            ))
          )}
        </ul>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={() => { wasDragging.current = true; }}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items.map(i => i.id)} strategy={rectSortingStrategy}>
            <ul className={cn(styles.categoryList, !isLoading && styles.categoryListLoaded)}>
              {isLoading ? (
                <CategorySkeleton count={6} />
              ) : (
                items.map(item => (
                  <Category key={item.id} data={item} onClick={deleteCategory} wasDragging={wasDragging} />
                ))
              )}
            </ul>
          </SortableContext>
        </DndContext>
      )}
      {!isLoading && page && setPage && (
        <Pagination pageCount={page} forcePage={currentPage} onPageChange={handleSetPage} />
      )}
    </div>
  );
};

export default CategoryList;
